import { useCallback, useEffect, useRef, useState } from "react";
import { VideoData, VideoStatus } from "../../../shared/interfaces/VideoData";
import { bodycamClient } from "@/shared/api/clients";
import { getTranscript } from "../../../shared/api/transcriptsApi";

export const useVideoData = (
  evidenceID: string | undefined,
  showError: (message: string) => void
) => {
  const [videoData, setVideoData] = useState<VideoData | null>(null);
  const [videoDataList, setVideoDataList] = useState<VideoData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewedVideos, setViewedVideos] = useState<Set<number>>(new Set([1]));
  const [loading, setLoading] = useState(true);

  // Bumped on every fetch; a response is applied only if its token is still the
  // latest, so a slow fetch for a previous evidenceID can't overwrite newer data.
  const requestIdRef = useRef(0);

  const fetchVideoData = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    const videoIDs = evidenceID?.split("~") || [];
    if (videoIDs.length === 0) {
      setLoading(false);
      return;
    }

    const videoPromises = videoIDs.map((videoID) =>
      bodycamClient.get("/evidence/details", {
        params: { evidenceId: videoID },
      })
    );

    Promise.all(videoPromises)
      .then(async (responses) => {
        const videos = responses.map((response) => response.data);
        // `/evidence/details` returns player metadata only — it never includes
        // the transcript body. Hydrate it here for already-transcribed videos so
        // VideoPreview's overlay + Edit/Download UI (gated on `transcript`) render.
        await Promise.allSettled(
          videos.map(async (video) => {
            if (video.status === VideoStatus.TRANSCRIPT_READY) {
              try {
                const { transcript } = await getTranscript(video.id);
                video.transcript = transcript;
              } catch {
                // Leave the transcript empty on failure; the overlay simply
                // won't render for this video and playback is unaffected.
              }
            }
          })
        );
        // A newer evidenceID fetch has superseded this one — drop the result.
        if (requestId !== requestIdRef.current) return;
        setVideoDataList(videos);
        setVideoData(videos[0]);
      })
      .catch((error) => {
        if (requestId !== requestIdRef.current) return;
        showError(
          error.response?.data?.error ||
            "An error occurred while fetching video data. Please try again later."
        );
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
      });
  }, [evidenceID, showError]);

  useEffect(() => {
    fetchVideoData();
  }, [fetchVideoData]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    setVideoData(videoDataList[page - 1]);
    setViewedVideos((prev) => new Set([...prev, page]));
  };

  const handleNextVideo = () => {
    if (currentPage < videoDataList.length) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      setVideoData(videoDataList[nextPage - 1]);
      setViewedVideos((prev) => new Set([...prev, nextPage]));
    }
  };

  const handlePrevVideo = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      setVideoData(videoDataList[prevPage - 1]);
    }
  };

  const totalVideos = videoDataList.length;
  const viewedCount = viewedVideos.size;
  const allVideosViewed = totalVideos <= 1 || viewedVideos.size >= totalVideos;
  const generateDisabled = totalVideos >= 2 && !allVideosViewed;
  // videoUrl is the viewability anchor: absent while the file isn't in S3.
  // Generation over a missing file can only fail server-side minutes later
  // (burning a job slot), so the page blocks it up front.
  const unavailableVideos = videoDataList.filter((video) => !video.videoUrl);
  // FAILED is terminal — bodycam never retries a failed fetch — so "check
  // again later" copy would be a lie for these. The rest are still on their
  // way down, where "check again later" is exactly right — counted apart so
  // a mixed selection can surface both messages.
  const failedVideoCount = unavailableVideos.filter(
    (video) => video.status === VideoStatus.FAILED,
  ).length;
  const pendingVideoCount = unavailableVideos.length - failedVideoCount;

  return {
    videoData,
    videoDataList,
    currentPage,
    viewedVideos,
    loading,
    viewedCount,
    totalVideos,
    allVideosViewed,
    generateDisabled,
    failedVideoCount,
    pendingVideoCount,
    setViewedVideos,
    setVideoData,
    setVideoDataList,
    setCurrentPage,
    handlePageChange,
    handleNextVideo,
    handlePrevVideo,
  };
};
